import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import EvidenceBadge from '@/components/EvidenceBadge';
import ContraindicationBox from '@/components/ContraindicationBox';
import TierGateCTA from '@/components/TierGateCTA';
import { ExternalLink, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { PROTO, STATE } from '@/constants/testIds/healthspan';

const labelize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export default function ProtocolDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/protocols/${id}`);
        setData(data);
      } catch (e) { setError(e.response?.data?.detail || 'Failed to load'); }
    })();
  }, [id]);

  if (error) return (
    <Card data-testid={STATE.error} className="p-6 rounded-[var(--radius-md)] border border-[color:var(--danger)] bg-[color:var(--danger-bg)] text-[color:var(--danger)]">{error}</Card>
  );
  if (!data) return <div className="space-y-3"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-40" /></div>;
  const p = data.protocol;
  const isResearcher = user?.role === 'researcher' || user?.role === 'admin';

  return (
    <div className="space-y-6">
      <Link to="/protocols" className="inline-flex items-center gap-1 text-sm text-[color:var(--muted-fg)] hover:text-[color:var(--fg)]"><ArrowLeft className="h-3.5 w-3.5" /> Back to catalogue</Link>
      <header className="rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)] p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{p.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-[color:var(--surface-2)] border border-[color:var(--border-color)] px-2.5 py-0.5 text-[11px] text-[color:var(--muted-fg)]">{labelize(p.mechanism)}</span>
              <span className="inline-flex items-center rounded-full bg-[color:var(--surface-2)] border border-[color:var(--border-color)] px-2.5 py-0.5 text-[11px] text-[color:var(--muted-fg)]">{labelize(p.intervention_type)}</span>
              <EvidenceBadge level={p.evidence_level} />
              {p.is_placeholder_citation && <span data-testid={PROTO.citationsPending} className="inline-flex items-center rounded-full border border-[color:var(--border-color)] px-2.5 py-0.5 text-[11px] text-[color:var(--muted-fg)]">Citations pending</span>}
            </div>
          </div>
          <a data-testid={PROTO.sourceLink} href={p.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[color:var(--accent-teal)] underline underline-offset-4">Primary source <ExternalLink className="h-3.5 w-3.5" /></a>
        </div>
      </header>

      <Tabs defaultValue="summary">
        <TabsList>
          <TabsTrigger value="summary">Summary</TabsTrigger>
          <TabsTrigger value="safety">Safety &amp; cautions</TabsTrigger>
          <TabsTrigger value="technical">Technical summary</TabsTrigger>
          <TabsTrigger value="targets">Biomarker targets</TabsTrigger>
        </TabsList>
        <TabsContent value="summary" className="mt-4">
          <Card className="p-6 rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)]">
            <p className="text-sm leading-7">{p.summary_public}</p>
            <p className="mt-4 text-xs text-[color:var(--muted-fg)]">Educational summary. Not medical advice.</p>
          </Card>
        </TabsContent>
        <TabsContent value="safety" className="mt-4 space-y-4">
          {p.safety_text && (
            <Card className="p-6 rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)]">
              <div className="text-sm font-semibold">Safety considerations</div>
              <p className="mt-2 text-sm leading-6 text-[color:var(--muted-fg)]">{p.safety_text}</p>
            </Card>
          )}
          <ContraindicationBox items={p.contraindications} />
        </TabsContent>
        <TabsContent value="technical" className="mt-4">
          {isResearcher ? (
            <Card className="p-6 rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)]">
              {p.summary_researcher ? (
                <p className="text-sm leading-7 whitespace-pre-line">{p.summary_researcher}</p>
              ) : (
                <p className="text-sm text-[color:var(--muted-fg)]">No extended technical summary available for this protocol yet.</p>
              )}
            </Card>
          ) : (
            <TierGateCTA />
          )}
        </TabsContent>
        <TabsContent value="targets" className="mt-4">
          <Card className="p-6 rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)]">
            <div className="text-sm font-semibold">Biomarker targets</div>
            {p.biomarker_targets?.length ? (
              <ul className="mt-2 grid grid-cols-2 gap-2 text-sm">
                {p.biomarker_targets.map(b => <li key={b} className="rounded-[var(--radius-sm)] border border-[color:var(--border-color)] bg-[color:var(--surface-2)] px-3 py-2">{labelize(b)}</li>)}
              </ul>
            ) : <p className="mt-2 text-sm text-[color:var(--muted-fg)]">No specific biomarker targets listed.</p>}
            <div className="mt-4 text-xs text-[color:var(--muted-fg)]">Applicability rules: {p.applicability_rules?.length ? p.applicability_rules.join(', ') : 'none'}</div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
